import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect } from 'react';
import {
  state as reactiveState,
  useReactiveState,
} from '@frameio/graphql-client';
import type {
  PaymentCard,
  PlanPeriod,
} from '@frameio/graphql-client/generated';
import CardFormContainer from '../../../../billing/components/CardFormContainer';
import { ACCOUNT_CONVERSION_STEPS } from '../constants';
import type { ConversionDialogProps } from '../types';
import { PlanPaymentSubmit } from './PlanPaymentSubmit';

type PlanPaymentProps = {
  onCancel: () => void;
  paymentMethod?: PaymentCard;
  previousStep?: ACCOUNT_CONVERSION_STEPS;
  setParentStep: Dispatch<SetStateAction<ACCOUNT_CONVERSION_STEPS>>;
} & Pick<ConversionDialogProps, 'period' | 'subscriptionUpdateSource'>;

export const PlanPayment = ({
  onCancel,
  paymentMethod,
  period,
  setParentStep,
  subscriptionUpdateSource,
  previousStep,
}: PlanPaymentProps) => {
  const cachedStripeData = useReactiveState(reactiveState.billing.stripe);
  const hasPaymentMethod =
    !!paymentMethod?.card ||
    !!(
      cachedStripeData?.addressElementValue && cachedStripeData?.stripeTokenId
    );

  const handleComplete = useCallback(() => {
    setParentStep(ACCOUNT_CONVERSION_STEPS.SUMMARY);
  }, [setParentStep]);

  /** If the user already has a paymentMethod stored, we go to the next step */
  useEffect(() => {
    if (hasPaymentMethod && previousStep !== ACCOUNT_CONVERSION_STEPS.SUMMARY) {
      handleComplete();
    }
  }, [handleComplete, hasPaymentMethod, previousStep]);

  return (
    <CardFormContainer>
      <PlanPaymentSubmit
        onCancel={onCancel}
        onComplete={handleComplete}
        paymentMethod={paymentMethod}
        period={period as PlanPeriod}
        subscriptionUpdateSource={subscriptionUpdateSource}
      />
    </CardFormContainer>
  );
};
